import logger from '@/utils/logger';
import { useEffect } from 'react';

interface GclidData {
    value: string;
    timestamp: number;
}

const GCLID_STORAGE_KEY = 'gclid_data';
const GCLID_EXPIRY_HOURS = 24;

const getValidGclid = (): string | null => {
    if (typeof window === 'undefined') return null;

    try {
        const storedData = localStorage.getItem(GCLID_STORAGE_KEY);
        if (!storedData) return null;

        const gclidData: GclidData = JSON.parse(storedData);
        const now = Date.now();
        const expiryTime = gclidData.timestamp + GCLID_EXPIRY_HOURS * 60 * 60 * 1000;

        if (now > expiryTime) {
            localStorage.removeItem(GCLID_STORAGE_KEY);
            return null;
        }

        return gclidData.value;
    } catch (error) {
        logger.error('Error retrieving gclid:', error);
        localStorage.removeItem(GCLID_STORAGE_KEY);
        return null;
    }
};

export const useGclidUrl = () => {
    useEffect(() => {
        const gclid = getValidGclid();
        if (gclid && !window.location.search.includes('gclid=')) {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('gclid', gclid);
            const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, []);
};

interface GoogleAdsProviderProps {
    children: React.ReactNode;
}

export const GoogleAdsProvider: React.FC<GoogleAdsProviderProps> = ({ children }) => {
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const urlParams = new URLSearchParams(window.location.search);
        const gclid = urlParams.get('gclid');

        if (gclid) {
            const gclidData: GclidData = {
                value: gclid,
                timestamp: Date.now()
            };
            localStorage.setItem(GCLID_STORAGE_KEY, JSON.stringify(gclidData));
        }
    }, []);

    return <>{children}</>;
};

export default GoogleAdsProvider;
